import React, { useEffect, useState, useMemo } from 'react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './ui/command';
import {
  MessageSquare,
  UserPlus,
  Users,
  ShieldAlert,
  ShieldCheck,
  Calculator,
  LogOut,
  Download,
  KeyRound,
  Clock,
  Database,
  Bell,
  Ban,
  AlertTriangle,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { loadRecentCommands, recordRecentCommand } from '../utils/recentCommands';

/**
 * CommandPalette — Cmd/Ctrl+K launcher.
 *
 * Actions:
 *  - Jump to any open conversation
 *  - Start DM with any contact
 *  - Create new chat / invite
 *  - Open settings
 *  - Run security scan
 *  - Activate decoy mode
 *  - Emergency wipe (panic)
 *  - Show desktop/mobile downloads
 *  - Sign out
 *
 * Keybinding is registered in ChatView / Dashboard higher up; this component just renders.
 */
export const CommandPalette = ({
  isOpen,
  onOpenChange,
  conversations,
  contacts,
  currentUserId,
  onOpenConversation,
  onStartDm,
  onOpenNewChat,
  onOpenSettings,
  onOpenLocalData,
  onOpenPushSettings,
  onOpenBlockedUsers,
  onOpenEmergency,
  onOpenSecurityScanner,
  onActivateDecoy,
  onPanic,
}) => {
  const { logout } = useAuth();
  const [query, setQuery] = useState('');
  const [recents, setRecents] = useState([]);

  // Load recents each time the palette opens
  useEffect(() => {
    if (isOpen) {
      setRecents(loadRecentCommands());
      setQuery('');
    }
  }, [isOpen]);

  // Build a name resolver for conversations
  const resolveConvName = useMemo(() => (conv) => {
    if (conv.type === 'group') return conv.encrypted_name || 'Group Chat';
    const otherId = conv.participants.find((p) => p !== currentUserId);
    const contact = contacts.find((c) => c.id === otherId);
    return contact?.username || 'Unknown';
  }, [contacts, currentUserId]);

  // Dedupe: don't show a contact in "Start DM" if a 1:1 conversation with them already exists
  const contactsWithoutConv = useMemo(() => {
    const directConvPeers = new Set(
      conversations
        .filter((c) => c.type !== 'group')
        .flatMap((c) => c.participants)
        .filter((id) => id !== currentUserId)
    );
    return contacts.filter((c) => !directConvPeers.has(c.id));
  }, [conversations, contacts, currentUserId]);

  /** Run a handler, record a recent entry, close the dialog. */
  const select = (meta, fn) => () => {
    if (meta) recordRecentCommand(meta);
    onOpenChange(false);
    setTimeout(() => fn?.(), 0);
  };

  /** Convert a stored recent into a runnable handler if it's still valid. */
  const resolveRecent = (r) => {
    if (r.type === 'conv') {
      const conv = conversations.find((c) => c.id === r.id);
      if (!conv) return null; // conversation was deleted
      return {
        label: resolveConvName(conv),
        icon: conv.type === 'group' ? Users : MessageSquare,
        handler: () => onOpenConversation?.(conv),
      };
    }
    if (r.type === 'dm') {
      const contact = contacts.find((c) => c.id === r.id);
      if (!contact) return null;
      return {
        label: contact.username,
        icon: UserPlus,
        handler: () => onStartDm?.(contact),
      };
    }
    if (r.type === 'action') {
      const map = {
        'new-chat': { label: 'New chat / Manage invites', icon: UserPlus, handler: onOpenNewChat },
        'security-scan': { label: 'Run security scan', icon: ShieldCheck, handler: onOpenSecurityScanner },
        'decoy': { label: 'Activate decoy mode', icon: Calculator, handler: onActivateDecoy },
        'settings': { label: 'Open settings', icon: KeyRound, handler: onOpenSettings },
        'local-data': { label: 'Local data inspector', icon: Database, handler: onOpenLocalData },
        'push-settings': { label: 'Push notification settings', icon: Bell, handler: onOpenPushSettings },
        'blocked-users': { label: 'Blocked users', icon: Ban, handler: onOpenBlockedUsers },
        'emergency': { label: 'Emergency wipe & decoy', icon: AlertTriangle, handler: onOpenEmergency },
        'downloads': { label: 'Get desktop / mobile app', icon: Download, handler: () => window.scrollTo({ top: 0 }) },
        'panic': { label: 'Emergency wipe (panic)', icon: ShieldAlert, handler: onPanic },
        'logout': { label: 'Sign out', icon: LogOut, handler: logout },
      };
      return map[r.id] || null;
    }
    return null;
  };

  // Resolve recents; show at most 5 valid ones; only when there's no search query
  const recentItems = !query
    ? recents
        .map((r) => ({ r, ui: resolveRecent(r) }))
        .filter(({ ui }) => !!ui)
        .slice(0, 5)
    : [];

  return (
    <CommandDialog open={isOpen} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Type a command or search..."
        value={query}
        onValueChange={setQuery}
        data-testid="command-palette-input"
      />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        {recentItems.length > 0 && (
          <>
            <CommandGroup heading="Recent">
              {recentItems.map(({ r, ui }) => {
                const Icon = ui.icon || Clock;
                return (
                  <CommandItem
                    key={`recent-${r.type}-${r.id}`}
                    value={`recent ${ui.label} ${r.id}`}
                    onSelect={select({ type: r.type, id: r.id, label: ui.label }, ui.handler)}
                    data-testid={`cmd-recent-${r.type}-${r.id}`}
                  >
                    <Icon className="mr-2 h-4 w-4 text-slate-400" />
                    <span>{ui.label}</span>
                    <Clock className="ml-auto w-3 h-3 text-slate-600" />
                  </CommandItem>
                );
              })}
            </CommandGroup>
            <CommandSeparator />
          </>
        )}

        {conversations.length > 0 && (
          <CommandGroup heading="Jump to">
            {conversations.map((conv) => {
              const name = resolveConvName(conv);
              return (
                <CommandItem
                  key={`conv-${conv.id}`}
                  value={`conv ${name} ${conv.id}`}
                  onSelect={select(
                    { type: 'conv', id: conv.id, label: name },
                    () => onOpenConversation?.(conv),
                  )}
                  data-testid={`cmd-conv-${conv.id}`}
                >
                  {conv.type === 'group' ? (
                    <Users className="mr-2 h-4 w-4 text-primary" />
                  ) : (
                    <MessageSquare className="mr-2 h-4 w-4 text-primary" />
                  )}
                  <span>{name}</span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {contactsWithoutConv.length > 0 && (
          <CommandGroup heading="Start DM">
            {contactsWithoutConv.map((c) => (
              <CommandItem
                key={`dm-${c.id}`}
                value={`dm ${c.username}`}
                onSelect={select(
                  { type: 'dm', id: c.id, label: c.username },
                  () => onStartDm?.(c),
                )}
                data-testid={`cmd-dm-${c.id}`}
              >
                <UserPlus className="mr-2 h-4 w-4 text-primary" />
                <span>{c.username}</span>
                <span className="ml-auto text-xs text-slate-500">
                  {c.is_online ? 'online' : 'offline'}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Actions">
          <CommandItem
            value="new chat invite"
            onSelect={select(
              { type: 'action', id: 'new-chat', label: 'New chat / Manage invites' },
              onOpenNewChat,
            )}
            data-testid="cmd-new-chat"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            New chat / Manage invites
          </CommandItem>
          <CommandItem
            value="security scanner scan"
            onSelect={select(
              { type: 'action', id: 'security-scan', label: 'Run security scan' },
              onOpenSecurityScanner,
            )}
            data-testid="cmd-security-scan"
          >
            <ShieldCheck className="mr-2 h-4 w-4" />
            Run security scan
          </CommandItem>
          <CommandItem
            value="decoy mode calculator"
            onSelect={select(
              { type: 'action', id: 'decoy', label: 'Activate decoy mode' },
              onActivateDecoy,
            )}
            data-testid="cmd-decoy"
          >
            <Calculator className="mr-2 h-4 w-4" />
            Activate decoy mode
            <CommandShortcut>Ctrl+Shift+D</CommandShortcut>
          </CommandItem>
          <CommandItem
            value="settings preferences recovery"
            onSelect={select(
              { type: 'action', id: 'settings', label: 'Open settings' },
              onOpenSettings,
            )}
            data-testid="cmd-settings"
          >
            <KeyRound className="mr-2 h-4 w-4" />
            Open settings
          </CommandItem>
          <CommandItem
            value="local data inspector storage audit backup restore"
            onSelect={select(
              { type: 'action', id: 'local-data', label: 'Local data inspector' },
              onOpenLocalData,
            )}
            data-testid="cmd-local-data"
          >
            <Database className="mr-2 h-4 w-4" />
            Local data inspector
            <span className="ml-auto text-[10px] text-slate-500">audit · backup · restore</span>
          </CommandItem>
          <CommandItem
            value="push notifications notify alerts"
            onSelect={select(
              { type: 'action', id: 'push-settings', label: 'Push notification settings' },
              onOpenPushSettings,
            )}
            data-testid="cmd-push-settings"
          >
            <Bell className="mr-2 h-4 w-4" />
            Push notification settings
          </CommandItem>
          <CommandItem
            value="blocked users contacts block unblock"
            onSelect={select(
              { type: 'action', id: 'blocked-users', label: 'Blocked users' },
              onOpenBlockedUsers,
            )}
            data-testid="cmd-blocked-users"
          >
            <Ban className="mr-2 h-4 w-4" />
            Blocked users
          </CommandItem>
          <CommandItem
            value="emergency wipe decoy panic"
            onSelect={select(
              { type: 'action', id: 'emergency', label: 'Emergency wipe & decoy' },
              onOpenEmergency,
            )}
            data-testid="cmd-emergency"
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Emergency wipe & decoy
          </CommandItem>
          <CommandItem
            value="download desktop mobile app"
            onSelect={select(
              { type: 'action', id: 'downloads', label: 'Get desktop / mobile app' },
              () => window.scrollTo({ top: 0 }),
            )}
            data-testid="cmd-download"
          >
            <Download className="mr-2 h-4 w-4" />
            Get desktop / mobile app
          </CommandItem>
        </CommandGroup>

        <CommandSeparator />

        <CommandGroup heading="Danger">
          <CommandItem
            value="panic wipe emergency"
            onSelect={select(
              { type: 'action', id: 'panic', label: 'Emergency wipe (panic)' },
              onPanic,
            )}
            className="text-red-400 data-[selected=true]:bg-red-500/10 data-[selected=true]:text-red-300"
            data-testid="cmd-panic"
          >
            <ShieldAlert className="mr-2 h-4 w-4" />
            Emergency wipe (panic)
          </CommandItem>
          <CommandItem
            value="sign out logout"
            onSelect={select(
              { type: 'action', id: 'logout', label: 'Sign out' },
              logout,
            )}
            data-testid="cmd-logout"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
